import type { IWorkPeriodTemplate } from "@/interfaces/api/work-period-template.interface.ts"
import type { IWorkPeriod } from "@/interfaces/api/work-period.interface.ts"
import type { IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface.ts"
import { BaseRepository } from "@/repositories/base-repository.ts"
import { HTTPMethods, HTTPRequestBuilder } from "@/builder/HTTPRequestBuilder.ts"
import type { AxiosResponse } from "axios"

export class WorkPeriodTemplateRepository extends BaseRepository<IWorkPeriodTemplate> {
  resourcePath = "administration/work-period-templates"

  /**
   * Atomically creates a work period (with its activities) from this template for the given date.
   * The server projects the template's time-only values onto the date and persists the work
   * period and its activities in a single transaction, returning the fully-nested work period.
   * @param templateId The id of the template to instantiate
   * @param date The date (YYYY-MM-DD) to project the template's times onto
   * @param config Optional request configuration (e.g. validation status-code handlers)
   * @param draft Optional corrected application values; does not update the saved template
   */
  public async createWorkPeriod(
    templateId: string,
    date: string,
    config: IRepositoryRequestConfig = {},
    draft?: IWorkPeriod
  ): Promise<AxiosResponse<IWorkPeriod>> {
    const resourcePath = `${this.getResourcePath(templateId, config)}create-work-period/`

    return await new HTTPRequestBuilder(HTTPMethods.POST, resourcePath)
      .setData({ date, ...(draft ? { draft } : {}) })
      .query(config.query)
      .setExpectedStatusCode(201)
      .setbeforeErrorHandlingCallback(config.beforeErrorHandlingCallback)
      .addStatusCodeHandlers(config.statusCodeHandlers)
      .build()
      .execute()
  }
}
